import axiosInstance from "../services/AxiosInstance"

const API_URL = "/mockinterviews"
const MOCK_INTERVIEWS_PAGE_CAP = 100
const MOCK_INTERVIEWS_DEFAULT_LIMIT = 10

// Create a new mock interview record
export const createmockinterviews = async (mockInterviewData) => {
  try {
    const response = await axiosInstance.post(API_URL, mockInterviewData)
    return response.data
  } catch (error) {
    console.error("Error creating mock interview", {
      error: error.response?.data,
      requestData: mockInterviewData,
    })
    throw error
  }
}

// Get all mock interviews with optional pagination and search
export const getmockinterviews = async (limit = 10, page = 1, search = "", includeTotal = true) => {
  try {
    const safeLimit = Math.max(1, Math.min(MOCK_INTERVIEWS_PAGE_CAP, Number.parseInt(limit, 10) || MOCK_INTERVIEWS_DEFAULT_LIMIT))
    const safePage = Math.max(1, Number.parseInt(page, 10) || 1)
    const safeSearch = search || ""
    const response = await axiosInstance.get(`${API_URL}?limit=${safeLimit}&page=${safePage}&search=${safeSearch}&includeTotal=${includeTotal ? "true" : "false"}`)
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    }
  } catch (error) {
    console.error("Error fetching mock interviews", error)
    throw error
  }
}

// Get mock interview evaluations for a specific event
export const getMockInterviewResultsByEvent = async (eveventId, batchId, trainerId) => {
  try {
    const params = new URLSearchParams({ limit: "100", page: "1" });
    if (eveventId) params.set("eveventId", eveventId);
    const response = await axiosInstance.get(`${API_URL}?${params.toString()}`);
    return response.data?.data || [];
  } catch (error) {
    console.error("Error fetching mock interview results for event", error);
    return [];
  }
};

// Get mock interview events for a specific trainer, paginated + searchable
// Endpoint: GET /mockinterviews/my-interview-events?trainerId=&page=&limit=&search=
export const getMyInterviewEvents = async (trainerId, page = 1, limit = 10, search = "") => {
  try {
    const safePage = Math.max(1, Number.parseInt(page, 10) || 1)
    const safeLimit = Math.max(1, Math.min(100, Number.parseInt(limit, 10) || 10))
    const params = new URLSearchParams({
      trainerId,
      page: String(safePage),
      limit: String(safeLimit),
    })
    if (search) params.set("search", search)
    const response = await axiosInstance.get(`${API_URL}/my-interview-events?${params.toString()}`)
    return {
      data: response.data.data || [],
      pagination: response.data.pagination,
      status: response.data.statusCode,
      message: response.data.message,
    }
  } catch (error) {
    console.error("Error fetching mock interview events", error)
    throw error
  }
}

// Get student attendance for a specific trainer, batch, and student
// Endpoint: GET /trainerdashboard/mystudentattendance?batchId=&studentId=&trainerId=
export const getStudentAttendance = async (batchId, studentId, trainerId) => {
  try {
    const response = await axiosInstance.get(
      `/trainerdashboard/mystudentattendance?batchId=${batchId}&studentId=${studentId}&trainerId=${trainerId}`
    )
    return {
      data: response.data.data || [],
      pagination: response.data.pagination,
      status: response.data.statusCode,
      message: response.data.message,
    }
  } catch (error) {
    console.error("Error fetching student attendance", error)
    throw error
  }
}

// Get student quiz submissions for a specific student, batch, and trainer
// Endpoint: GET /trainerdashboard/mystudentquizes?studentId=&batchId=&trainerId=
export const getStudentQuizzes = async (studentId, batchId, trainerId) => {
  try {
    const response = await axiosInstance.get(
      `/trainerdashboard/mystudentquizes?studentId=${studentId}&batchId=${batchId}&trainerId=${trainerId}`
    )
    return {
      data: response.data.data || [],
      pagination: response.data.pagination,
      status: response.data.statusCode,
      message: response.data.message,
    }
  } catch (error) {
    console.error("Error fetching student quizzes", error)
    throw error
  }
}


export const getStudentMockInterviewResult = async (trainerId, batchId, studentId, eveventId) => {
  try {
    const response = await axiosInstance.get(
      `${API_URL}/myInterviewresult?trainerId=${trainerId}&batchId=${batchId}&studentId=${studentId}&eveventId=${eveventId}`
    )
    return {
      data: response.data.data || null,
      status: response.data.statusCode,
      message: response.data.message,
    }
  } catch (error) {
    console.error("Error fetching student mock interview result", error)
    throw error
  }
}

// Submit or update a student's mock interview evaluation result
export const submitStudentMockInterviewResult = async (resultData) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/myInterviewresult`, resultData)
    return response.data
  } catch (error) {
    console.error("Error submitting mock interview result", {
      error: error.response?.data,
      requestData: resultData,
    })
    throw error
  }
}
// Update a mock interview by ID
export const updatemockinterviews = async (mockInterviewId, mockInterviewData) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${mockInterviewId}`, mockInterviewData)
    return response.data
  } catch (error) {
    console.error("Error updating mock interview", error)
    throw error
  }
}

// Delete a mock interview by ID
export const deletemockinterviews = async (mockInterviewId) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/${mockInterviewId}`)
    return response.data
  } catch (error) {
    console.error("Error deleting mock interview", error)
    throw error
  }
}

// Get a mock interview by ID
export const getmockinterviewsById = async (mockInterviewId) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/${mockInterviewId}`)
    return {
      data: response.data.data,
      status: response.status,
      message: response.data.message,
    }
  } catch (error) {
    console.error("Error fetching mock interview details", error)
    throw error
  }
}

// Get mock interview events & evaluations for a student (with summary metrics)
// Endpoint: GET /mockinterviews/student-interviews?studentId=&email=&search=&status=&page=&limit=
export const getStudentMockInterviews = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.studentId) queryParams.set("studentId", params.studentId)
    if (params.email) queryParams.set("email", params.email)
    if (params.userId) queryParams.set("userId", params.userId)
    if (params.search) queryParams.set("search", params.search)
    if (params.status) queryParams.set("status", params.status)
    if (params.page) queryParams.set("page", String(params.page))
    if (params.limit) queryParams.set("limit", String(params.limit))

    const response = await axiosInstance.get(`${API_URL}/student-interviews?${queryParams.toString()}`)
    return {
      data: response.data?.data || [],
      metrics: response.data?.metrics || {
        totalInterviews: 0,
        evaluatedCount: 0,
        qualifiedCount: 0,
        upcomingCount: 0,
        averageScore: 0,
      },
      student: response.data?.student || null,
      pagination: response.data?.pagination || {},
      status: response.data?.statusCode,
      message: response.data?.message,
    }
  } catch (error) {
    console.error("Error fetching student mock interviews", error)
    throw error
  }
}